import { useField, useFormikContext } from 'formik';
import { useRef, FocusEvent, useEffect, ChangeEvent, useState } from 'react';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useLocale } from '../../../../context/Localization/localization';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { PhonePatternMask } from '../PhonePatternMask/PhonePatternMask';
import { WebFormPhoneFieldProps } from '../FormPhoneField.types';
import { PhoneFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const WebFormPhoneField = ({
    name,
    id,
    title,
    label,
    ariaLabel,
    disabled,
    required,
    autoFocus,
    capsLock,
    fullWidth,
    seventyFivePercentWidth,
    initiallyMasked,
    onChangeCallback,
    tabFocusRefContext,
    noValidation,
}: WebFormPhoneFieldProps) => {
    const [field, meta] = useField({
        name: name,
        validate: noValidation ? undefined : PhoneFieldValidation({
            required: required ?? false,
            label,
            name,
        }),
    });
    const { submitCount, setFieldValue } = useFormikContext();

    /**
     * A ref to the HTMLInputElement in the MUI TextField.
     */
    const inputRef = useRef<HTMLInputElement>(null);
    /**
     * Intended to ensure the masking is handled correctly by ensuring a re-render.
     * May not be needed.
     */
    const [value, setValue] = useState<string>(field.value ?? '');
    /**
     * Boolean state to update the email pattern mask once the field has been focused
     * so that it never remasks the value.
     */
    const [wasUnmasked, setWasUnmasked] = useState<boolean>(false);
    /**
     * Used to ensure that the mask re-runs when the field is focused or blurred
     * so that it correctly unmasks the email.
     */
    const [isFocused, setIsFocused] = useState<boolean>(false);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    const {
        dealer: { primaryLanguage },
    } = useKioskState();
    const locale = useLocale();

    useEffect(() => {
        if (tabFocusRefContext) {
            tabFocusRefContext.current = inputRef.current;
        }
    }, [tabFocusRefContext]);

    const handleOnBlur = () => {
        setIsFocused(false);
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChangeCallback && onChangeCallback();
        setValue(event.target.value);
        setFieldValue(name, event.target.value);
    };

    const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (initiallyMasked && !isFocused && !wasUnmasked) {
            // Handles unmasking if the value was masked.
            setFieldValue(name, '');
            setValue('');
        }
        if (!wasUnmasked) {
            // Handles preventing the field from remasking after the field has been focused once.
            setWasUnmasked(true);
        }
        setIsFocused(true);
    };

    return (
        <BaseTextField
            name={name}
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            inputMode={'tel'}
            type={'tel'}
            inputRef={inputRef}
            value={value}
            error={meta.error}
            touched={meta.touched || submitCount > 0}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            capsLock={capsLock}
            onFocus={handleOnFocus}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            InputProps={{
                inputComponent: PhonePatternMask as any,
                inputProps: {
                    isMasked: initiallyMasked && !isFocused && !wasUnmasked,
                },
            }}
        />
    );
};
